import { useMemo } from 'react'
import Delete from '@mui/icons-material/Delete'
import Edit from '@mui/icons-material/Edit'
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material'
import { QueryKey } from '@tanstack/react-query'
import useOverlay from '@/hooks/useOverlay'
import usePolicyPositionOptions from '@/hooks/usePolicyPositionOptions'
import useUsersById from '@/hooks/useUsersById'
import {
  useDeleteApprovalPolicy,
  useUpdateApprovalPolicy,
} from '@/service-library/hooks/approval-policies'
import { ApprovalPolicy } from '@/types/approvals'
import { approverRefKey, orderedApproverRefs } from '@/utils/approver-entries'
import { showErrorSnackbar, showSuccessSnackbar } from '@/utils/snackbars'
import PositionAvatar from '@/components/avatars/PositionAvatar'
import UserAvatar from '@/components/avatars/UserAvatar'
import DeleteApprovalPolicyDialog from './DeleteApprovalPolicyDialog'

type ApprovalPolicyCardProps = {
  policy: ApprovalPolicy
  listQueryKey: QueryKey
  onEdit: () => void
}

function requirementNote(policy: ApprovalPolicy): string {
  const count = policy.approvers.length
  if (policy.approval_order === 'sequential') {
    return `All ${count} must approve`
  }
  if (policy.approvals_required === null) {
    return `All ${count} approvers required`
  }
  const required = policy.approvals_required
  return `${required} approval${required === 1 ? '' : 's'} required`
}

export default function ApprovalPolicyCard({
  policy,
  listQueryKey,
  onEdit,
}: ApprovalPolicyCardProps) {
  const deleteOverlay = useOverlay()
  const isSequential = policy.approval_order === 'sequential'

  const refs = useMemo(() => orderedApproverRefs(policy.approvers), [policy])
  const hasUserApprover = refs.some((ref) => ref.kind === 'user')

  const { usersById, isPending: usersPending } = useUsersById({
    enabled: hasUserApprover,
  })

  const hasPositionApprover = refs.some((ref) => ref.kind === 'position')

  const { positionOptionsById, isPending: positionsPending } =
    usePolicyPositionOptions({ enabled: hasPositionApprover })

  const { updateApprovalPolicy, isPending: isUpdating } =
    useUpdateApprovalPolicy({
      listQueryKey,
      onError: () => {
        showErrorSnackbar(
          'Failed to update approval policy. Please try again later.',
        )
      },
    })

  const { deleteApprovalPolicy } = useDeleteApprovalPolicy({
    listQueryKey,
    onSuccess: () => {
      showSuccessSnackbar('Approval Policy Deleted')
    },
    onError: () => {
      showErrorSnackbar(
        'Failed to delete approval policy. Please try again later.',
      )
    },
  })

  return (
    <Card>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={2}
        sx={{ px: 2, py: 1.5 }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography variant="h6">{policy.description}</Typography>
          <Chip
            size="small"
            label={isSequential ? 'SEQUENTIAL' : 'ANY ORDER'}
          />
          {!policy.is_enabled && (
            <Tooltip
              title="While disabled, this policy won't be applied to any new documents. Documents it's already applied to still require it."
              enterDelay={500}
            >
              <Chip size="small" color="default" label="DISABLED" />
            </Tooltip>
          )}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center">
          <Switch
            checked={policy.is_enabled}
            disabled={isUpdating}
            onChange={() =>
              updateApprovalPolicy({
                ...policy,
                is_enabled: !policy.is_enabled,
              })
            }
            inputProps={{
              'aria-label': `${policy.description} enabled`,
            }}
          />
          <Button variant="text" startIcon={<Edit />} onClick={onEdit}>
            Edit
          </Button>
          <Button
            variant="text"
            color="error"
            startIcon={<Delete />}
            onClick={() => deleteOverlay.open()}
          >
            Delete
          </Button>
        </Stack>
      </Stack>

      <Divider />

      <Box sx={{ px: 2, py: 2, opacity: policy.is_enabled ? 1 : 0.5 }}>
        <Stack spacing={2}>
          <Stack spacing={1}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: 'uppercase', fontWeight: 600 }}
            >
              Conditions
            </Typography>
            <Typography variant="body2">{policy.llm_trigger_prompt}</Typography>
          </Stack>

          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="baseline">
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', fontWeight: 600 }}
              >
                Approvers
              </Typography>
              {/* With one approver every variant of the note is noise. */}
              {policy.approvers.length > 1 && (
                <Typography variant="caption" color="text.secondary">
                  — {requirementNote(policy)}
                </Typography>
              )}
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              useFlexGap
              sx={{ flexWrap: 'wrap' }}
            >
              {refs.map((ref, index) => {
                const key = approverRefKey(ref)
                const position =
                  ref.kind === 'position'
                    ? positionOptionsById.get(ref.positionId)
                    : undefined
                const user =
                  ref.kind === 'user' ? usersById.get(ref.userId) : undefined
                const isLoading =
                  ref.kind === 'position'
                    ? !position && positionsPending
                    : !user && usersPending

                const arrow = index > 0 && isSequential && (
                  <Typography aria-hidden="true" color="text.disabled">
                    &rarr;
                  </Typography>
                )

                if (isLoading) {
                  return (
                    <Stack
                      key={key}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                    >
                      {arrow}
                      <Skeleton variant="rounded" width={100} height={32} />
                    </Stack>
                  )
                }

                const name =
                  ref.kind === 'position'
                    ? position?.position.name ?? 'Unknown position'
                    : user?.name || 'Unknown user'

                return (
                  <Stack
                    key={key}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                  >
                    {arrow}
                    <Chip
                      avatar={
                        ref.kind === 'position' ? (
                          <PositionAvatar />
                        ) : (
                          <UserAvatar name={user?.name} />
                        )
                      }
                      label={isSequential ? `${index + 1}. ${name}` : name}
                      title={
                        ref.kind === 'position'
                          ? position?.ruleSummary || undefined
                          : user?.email
                      }
                      // A box rather than a pill, so the chip takes the same
                      // corners as the badge inside it and a position reads as
                      // a position before the name is even read.
                      sx={
                        ref.kind === 'position'
                          ? { borderRadius: 1 }
                          : undefined
                      }
                    />
                  </Stack>
                )
              })}
            </Stack>
          </Stack>
        </Stack>
      </Box>

      <DeleteApprovalPolicyDialog
        overlay={deleteOverlay}
        policyLabel={policy.description}
        onDelete={() => deleteApprovalPolicy(policy.id)}
      />
    </Card>
  )
}
